import '.././ui-native/Dropdown.scss';
import * as React from 'react';
import * as ReactDOM from 'react-dom';
import { cs } from '../utils/utils-classname';
import { Manager, Popper, Reference } from 'react-popper';
import { OutsideClick } from '@shared/ui-react/react-outside-click-handler';
import './Dropdown.scss';
import { Tooltip } from '@shared/ui-react/Tooltip';

const tooltipModifiers = [
    {
        name: 'offset',
        enabled: true,
        options: {
            offset: [0, 13],
        },
    },
];

const instances = new Set<Dropdown>();

export class Dropdown extends React.Component<{
    className?: string;
    bodyClassName?: string;
    styledBody?: boolean;
    disabled?: boolean;
    closeOnBodyClick?: boolean;
    onClose?: () => any;
    onOpen?: () => any;
    onToggle?: (boolean) => any;
    isOpen?: boolean;
    lazyRender?: boolean;
    buttonElem?: string;
    tooltip?: string;
    title?: string;
    triggerStyle?: React.CSSProperties;
    buttonStyle?: React.CSSProperties;
    bodyStyle?: React.CSSProperties;
    position?: 'left' | 'right';
    minHeight?: number;
    stopPropagation?: boolean;
    children?: React.ReactNode;

    buttonTemplate?: (props: object) => React.ReactNode;
    getBody?: Function;
}> {
    static defaultProps = {
        className: 'ik-button-secondary',
        closeOnBodyClick: false,
        lazyRender: true,
        buttonElem: 'button',
        position: 'left',
        styledBody: true,
        triggerStyle: {},
        bodyStyle: {},
        minHeight: 300,
    };

    state = {
        isOpen: false,
    };

    private bodyRef: HTMLDivElement;
    private buttonRef: HTMLElement;
    private resizeObserver: ResizeObserver;
    private schedulePopperUpdate: () => void;

    componentDidMount() {
        instances.add(this);
    }

    componentWillUnmount() {
        instances.delete(this);
        this.resizeObserver?.disconnect();
    }

    render() {
        const { lazyRender, triggerStyle, tooltip } = this.props;
        const isOpen = this.isOpen();

        const bodyClass = cs({
            'dropdown-react-wrapper': this.props.styledBody,
            'dropdown-off': !isOpen,
        });

        const buttonClass = cs(this.props.className, 'dropdown-toggle-btn', { 'dropdown-open': isOpen });
        const ButtonElem = this.props.buttonElem as any;

        const Btn = this.props.buttonTemplate ? (
            this.props.buttonTemplate({
                ref: el => (this.buttonRef = el),
                onClick: this.toggle,
                disabled: this.props.disabled,
            })
        ) : (
            <ButtonElem
                ref={el => (this.buttonRef = el)}
                type='button'
                className={buttonClass}
                title={this.props.title ?? null}
                style={this.props.buttonStyle}
                disabled={this.props.disabled}
                onClick={this.toggle}
            >
                {React.Children.toArray(this.props.children)[0]}
            </ButtonElem>
        );

        return (
            <Manager>
                <Reference>
                    {({ ref }) => (
                        <span ref={ref} style={{ display: 'inline-flex', ...triggerStyle }} className='dropdown-react-trigger'>
                            {tooltip ? (
                                <Tooltip tooltip={tooltip} placement='top' hideArrow={false} modifiers={tooltipModifiers} portalContainer={document.body}>
                                    {Btn}
                                </Tooltip>
                            ) : (
                                Btn
                            )}
                        </span>
                    )}
                </Reference>

                {ReactDOM.createPortal(
                    <Popper {...this.getPopperProps()} innerRef={this.handlePopperRef}>
                        {({ placement, ref, style, scheduleUpdate }) => {
                            this.schedulePopperUpdate = scheduleUpdate;
                            return !isOpen && lazyRender ? null : (
                                <div
                                    ref={ref}
                                    className={this.props.bodyClassName}
                                    style={{
                                        display: isOpen ? 'block' : 'none',
                                        zIndex: 10000, // <dialog> zindex + 1
                                        ...style,
                                    }}
                                    data-placement={placement}
                                >
                                    <OutsideClick onOutsideClick={e => this.onOutsideClick(e, scheduleUpdate)}>
                                        <div className={bodyClass} onClick={this.onBodyClick} style={this.getBodyStyle(placement)}>
                                            {this.props?.getBody ? this.props?.getBody() : React.Children.toArray(this.props.children)[this.props.buttonTemplate ? 0 : 1]}
                                        </div>
                                    </OutsideClick>
                                </div>
                            );
                        }}
                    </Popper>,
                    document.body
                )}
            </Manager>
        );
    }

    toggle = e => {
        if (this.props.stopPropagation) {
            e.stopPropagation();
        }
        this.isOpen() ? this.close() : this.open();
    };

    open = () => {
        if (typeof this.props.onToggle == 'function') {
            this.props.onToggle(true);
        } else {
            this.setState({
                isOpen: true,
            });

            if (typeof this.props.onOpen === 'function') {
                this.props.onOpen();
            }

            Array.from(instances).forEach(i => {
                if (i !== this && i.isOpen() && !i.bodyRef?.contains(this.buttonRef) /* not my parent*/) {
                    console.log('close', i.buttonRef);
                    i.close(true);
                }
            });
        }
    };

    close = (silent = false) => {
        if (!silent && typeof this.props.onClose === 'function') {
            this.props.onClose();
        }

        if (typeof this.props.onToggle == 'function') {
            this.props.onToggle(false);
        } else {
            this.setState({
                isOpen: false,
            });
        }
    };

    private isControlled() {
        return typeof this.props.isOpen !== 'undefined';
    }

    private isOpen() {
        return this.isControlled() ? this.props.isOpen : this.state.isOpen;
    }

    private getPopperProps = () => {
        return {
            placement: this.getPreferredPlacement(),
            positionFixed: true,
            modifiers: {
                preventOverflow: {
                    boundariesElement: 'viewport' as any,
                    escapeWithReference: false,
                },
                flip: {
                    enabled: true,
                },
                minHeightPlacement: {
                    enabled: true,
                    order: 625,
                    fn: this.forcePreferredPlacement,
                },
            },
        };
    };

    private getPreferredPlacement = () => {
        return this.getPlacement(this.getBestVerticalPlacement(this.props.minHeight));
    };

    private getBestVerticalPlacement = (requiredHeight?: number): 'top' | 'bottom' => {
        const space = this.getAvailableSpace();

        if (!space || !requiredHeight) {
            return 'bottom';
        }

        if (space.below >= requiredHeight) {
            return 'bottom';
        }

        if (space.above >= requiredHeight) {
            return 'top';
        }

        return space.above > space.below ? 'top' : 'bottom';
    };

    private getPlacement = (verticalPlacement: 'top' | 'bottom') => {
        const horizontalPlacement = this.props.position === 'left' ? 'start' : 'end';
        return `${verticalPlacement}-${horizontalPlacement}` as any;
    };

    private forcePreferredPlacement = data => {
        const forcedPlacement = this.getForcedPlacement(data);

        if (!forcedPlacement) {
            return data;
        }

        if (data.placement === forcedPlacement) {
            return data;
        }

        data.placement = forcedPlacement;

        if (data.offsets?.reference && data.offsets?.popper) {
            if (forcedPlacement.startsWith('top')) {
                data.offsets.popper.top = data.offsets.reference.top - data.offsets.popper.height;
            } else {
                data.offsets.popper.top = data.offsets.reference.bottom;
            }
        }

        return data;
    };

    private getForcedPlacement = data => {
        const space = this.getAvailableSpace();

        if (!space) {
            return null;
        }

        const requiredHeight = this.getRequiredHeight(data);

        if (this.props.minHeight && space.below < this.props.minHeight) {
            return this.getPlacement(this.getBestVerticalPlacement(this.props.minHeight));
        }

        if (requiredHeight && space.below < requiredHeight && space.above < requiredHeight) {
            return this.getPlacement(this.getBestVerticalPlacement(requiredHeight));
        }

        return null;
    };

    private getRequiredHeight = data => {
        const bodyContentHeight = (this.bodyRef?.querySelector('.dropdown-react-wrapper') as HTMLElement)?.scrollHeight || 0;
        const popperHeight = data.offsets?.popper?.height || 0;
        return Math.max(this.props.minHeight || 0, bodyContentHeight, popperHeight);
    };

    private getBodyStyle = (placement: string): React.CSSProperties => {
        const bodyStyle = this.props.bodyStyle || {};
        const maxHeight = this.getMaxHeight(placement);

        if (!maxHeight) {
            return bodyStyle;
        }

        return {
            ...bodyStyle,
            maxHeight,
        };
    };

    private getMaxHeight = (placement: string) => {
        const space = this.getAvailableSpace();

        if (!space) {
            return null;
        }

        const viewportPadding = 8;

        if (placement?.startsWith('top')) {
            return Math.max(20, space.above - viewportPadding);
        }

        return Math.max(20, space.fromTop - viewportPadding);
    };

    private getAvailableSpace = () => {
        if (!this.buttonRef || typeof window === 'undefined') {
            return null;
        }

        const triggerRect = this.buttonRef.getBoundingClientRect();
        return {
            above: triggerRect.top,
            below: window.innerHeight - triggerRect.bottom,
            fromTop: window.innerHeight - triggerRect.top,
        };
    };

    private handlePopperRef = (el: HTMLDivElement) => {
        if (this.bodyRef === el) {
            return;
        }

        this.bodyRef = el;
        this.resizeObserver?.disconnect();

        if (el && typeof ResizeObserver !== 'undefined') {
            this.resizeObserver = new ResizeObserver(() => {
                if (this.isOpen()) {
                    this.schedulePopperUpdate?.();
                }
            });
            this.resizeObserver.observe(el);
        }
    };

    private readonly onBodyClick = e => {
        if (this.props.stopPropagation) {
            e.stopPropagation();
        }
        if (this.props.closeOnBodyClick) this.close();
    };

    private readonly onOutsideClick = (e, scheduleUpdate) => {
        scheduleUpdate();
        if (this.isOpen() && !this.buttonRef?.contains(e.target)) {
            const clickedInsideDropdown =
                !!e.target.closest('.dropdown-react-wrapper') ||
                !!e.target.closest('.react-datepicker-popper') /* we can have datepickers inside dropdowns which are appended to <body> */ ||
                !!e.target.closest('.p-component'); /* allow clicking inside primeng components */

            // dont close if clicked inside any dropdown, to allow nested dropdowns
            if (!clickedInsideDropdown) {
                this.close(false);
            }
        }
    };
}

export class DropdownSelect extends React.Component<{
    items: { label: string; value: any }[];
    value: any;
    showRadio?: boolean;
    showCaret?: boolean;
    onChange: (value) => void;
}> {
    render() {
        const foundItem = this.props.items.filter(i => i.value == this.props.value)[0];
        return (
            <Dropdown closeOnBodyClick={true} buttonElem='div' className='ik-dropdown-toggle'>
                <span>
                    {foundItem ? foundItem.label : ' / '} {this.props.showCaret ? <i className='iconf-caret'></i> : null}
                </span>
                <ul className='clickable'>
                    {this.props.items.map(i => (
                        <li onClick={() => this.handleSelect(i.value)} key={i.value}>
                            {this.props.showRadio ? <input type='radio' checked={i.value == this.props.value} readOnly={true} /> : null} {i.label}
                        </li>
                    ))}
                </ul>
            </Dropdown>
        );
    }

    handleSelect = value => {
        this.props.onChange(value);
    };
}
